import type { AvatarSessionStatus } from '../../session';
import type { LivePromptOption } from '../hooks/useLiveOverlayState';
import { LiveFormInput, type LiveFormField } from './LiveFormInput';

/**
 * The Visual IVR overlay is split into two visual zones so the avatar's
 * face is never occluded by interactive UI:
 *
 *   - 'chat'     → top of the screen. Status badge, the assistant's
 *                  spoken text bubble, and the user's transcript bubble.
 *                  Read-only; pointer-events disabled (so the user can
 *                  still touch through to the avatar canvas).
 *
 *   - 'controls' → bottom of the screen, anchored above the mobile URL
 *                  bar / safe area. Option buttons and the form input.
 *                  These are the only INTERACTIVE elements, so they
 *                  belong below the face (where the user's thumb sits)
 *                  rather than over it.
 *
 * Render this component twice from App.tsx — once with zone='chat' and
 * once with zone='controls' — so the same overlay state drives both
 * zones consistently.
 *
 * Regression context (Apr 2026): when Visual IVR moved from the dead
 * builder VisualChannel to the avatar runtime host, all overlay copy
 * was bunched into a single absolutely-positioned column at the top of
 * the page. Result: option buttons and form inputs were rendered
 * directly over the avatar's eyes/nose/mouth, which (a) hid the face
 * the user came here to see, and (b) put the buttons way above the
 * thumb-reach zone on a phone. Splitting fixes both at once.
 */
export type LiveVoiceOverlayZone = 'chat' | 'controls';

interface LiveVoiceOverlayProps {
  zone?: LiveVoiceOverlayZone;
  /** Pixel offset from the bottom for the controls zone (covers the
   *  iOS Safari URL bar + safe-area inset). Defaults adapt to compact
   *  viewports. Ignored for the chat zone. */
  bottomOffsetPx?: number;
  liveConnectionError: string | null;
  liveInlineAssistantText: string;
  livePromptFields?: LiveFormField[];
  livePromptOptions: LivePromptOption[];
  liveSessionStatus?: AvatarSessionStatus;
  liveStatusLabel: string;
  liveUserTranscript: string;
  liveUserTranscriptFinal: boolean;
  onFieldSubmit?: (values: Record<string, string>) => void;
  onPromptOptionSelect: (value: string, label: string) => void;
  suppressEmbeddedLiveCopy: boolean;
}

function isCompactViewport(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < 768;
}

export function LiveVoiceOverlay({
  zone = 'chat',
  bottomOffsetPx,
  liveConnectionError,
  liveInlineAssistantText,
  livePromptFields,
  livePromptOptions,
  liveSessionStatus,
  liveStatusLabel,
  liveUserTranscript,
  liveUserTranscriptFinal,
  onFieldSubmit,
  onPromptOptionSelect,
  suppressEmbeddedLiveCopy
}: LiveVoiceOverlayProps) {
  const compact = isCompactViewport();
  const sessionEnded = liveSessionStatus === 'ended' || liveSessionStatus === 'error';

  if (zone === 'controls') {
    const hasOptions = livePromptOptions.length > 0 && !sessionEnded;
    const hasForm = Boolean(livePromptFields && livePromptFields.length > 0 && !sessionEnded && onFieldSubmit);
    if (!hasOptions && !hasForm) {
      return null;
    }
    // Anchor above the iOS URL bar / home indicator. `env(safe-area-inset-bottom)`
    // adds the dynamic safe-area inset Apple exposes when viewport-fit=cover
    // is set on the meta viewport (we set that in runtime-host.html).
    const resolvedBottomOffset = bottomOffsetPx ?? (compact ? 24 : 32);
    return (
      <div
        data-testid="live-voice-overlay-controls"
        style={{
          position: 'fixed',
          left: '12px',
          right: '12px',
          bottom: `calc(${resolvedBottomOffset}px + env(safe-area-inset-bottom, 0px))`,
          zIndex: 11,
          display: 'flex',
          flexDirection: 'column',
          gap: compact ? '10px' : '12px',
          alignItems: 'stretch',
          pointerEvents: 'none',
          maxWidth: compact ? undefined : '560px',
          marginLeft: compact ? undefined : 'auto',
          marginRight: compact ? undefined : 'auto'
        }}
      >
        {hasOptions && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              justifyContent: 'center',
              pointerEvents: 'auto'
            }}
          >
            {livePromptOptions.map((option) => (
              <button
                key={`${option.value}:${option.label}`}
                type="button"
                onClick={() => {
                  onPromptOptionSelect(option.value, option.label);
                }}
                style={{
                  border: '1px solid rgba(126, 186, 255, 0.32)',
                  background: 'rgba(12, 38, 72, 0.92)',
                  color: '#f7fbff',
                  borderRadius: '999px',
                  padding: compact ? '12px 18px' : '11px 18px',
                  fontSize: compact ? '15px' : '14px',
                  lineHeight: 1.2,
                  fontWeight: 500,
                  cursor: 'pointer',
                  boxShadow: '0 12px 30px rgba(0,0,0,0.32)',
                  backdropFilter: 'blur(12px)',
                  // iOS-friendly tap target — at least 44px tall.
                  minHeight: '44px'
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
        {hasForm && onFieldSubmit && (
          <div style={{ pointerEvents: 'auto' }}>
            <LiveFormInput
              fields={livePromptFields!}
              onSubmit={onFieldSubmit}
            />
          </div>
        )}
      </div>
    );
  }

  // zone === 'chat' — top of the screen, read-only.
  const hasStatus = !suppressEmbeddedLiveCopy || liveConnectionError;
  const hasAssistant = !suppressEmbeddedLiveCopy && liveInlineAssistantText;
  const hasTranscript = !suppressEmbeddedLiveCopy && liveUserTranscript;
  if (!hasStatus && !hasAssistant && !hasTranscript) {
    return null;
  }
  return (
    <div
      data-testid="live-voice-overlay-chat"
      style={{
        position: 'absolute',
        top: '12px',
        left: '12px',
        right: '12px',
        maxWidth: '560px',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: compact ? '8px' : '10px',
        pointerEvents: 'none'
      }}
    >
      {hasStatus && (
        <div style={{
          alignSelf: 'flex-start',
          background: 'rgba(10, 18, 34, 0.82)',
          border: '1px solid rgba(92, 169, 255, 0.18)',
          borderRadius: '999px',
          padding: '6px 12px',
          color: '#cfe3ff',
          fontSize: compact ? '11px' : '12px',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          backdropFilter: 'blur(10px)'
        }}>
          {liveStatusLabel}
        </div>
      )}
      {hasAssistant && (
        <div style={{
          alignSelf: 'flex-start',
          width: 'fit-content',
          maxWidth: compact ? 'calc(100vw - 20px)' : '420px',
          background: 'rgba(6, 12, 24, 0.86)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '14px',
          padding: compact ? '10px 12px' : '12px 14px',
          boxShadow: '0 10px 28px rgba(0,0,0,0.28)',
          backdropFilter: 'blur(12px)'
        }}>
          <div style={{
            color: '#7fb2ff',
            fontSize: compact ? '11px' : '12px',
            fontWeight: 600,
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.04em'
          }}>Assistant</div>
          <div style={{
            color: '#f7fbff',
            fontSize: compact ? '14px' : '15px',
            lineHeight: 1.45,
            whiteSpace: 'pre-wrap'
          }}>{liveInlineAssistantText}</div>
        </div>
      )}
      {hasTranscript && (
        <div style={{
          alignSelf: 'flex-end',
          maxWidth: '92%',
          background: 'rgba(18, 52, 94, 0.92)',
          border: '1px solid rgba(126, 186, 255, 0.24)',
          borderRadius: '14px',
          padding: compact ? '10px 12px' : '12px 14px',
          boxShadow: '0 10px 28px rgba(0,0,0,0.24)',
          backdropFilter: 'blur(12px)'
        }}>
          <div style={{
            color: '#b9dcff',
            fontSize: compact ? '11px' : '12px',
            fontWeight: 600,
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.04em'
          }}>
            {liveUserTranscriptFinal ? 'You said' : 'You are saying'}
          </div>
          <div style={{
            color: '#ffffff',
            fontSize: compact ? '14px' : '15px',
            lineHeight: 1.45,
            whiteSpace: 'pre-wrap'
          }}>{liveUserTranscript}</div>
        </div>
      )}
    </div>
  );
}

export default LiveVoiceOverlay;
